import { useEffect } from 'react';

import { useQueryStates } from 'nuqs';
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v7';
import { useForm } from 'react-hook-form';

import { parseDateRangeQuery, serializeDateRangeQuery } from '@crm/shared/lib';

import { DEALS_TABLE_ALLOW_SORT_CLEAR, SEARCH_QUERY_PARAM } from './consts';
import {
    DEFAULT_POSTPONED_DEALS_DIRECTION,
    DEFAULT_POSTPONED_DEALS_SORT,
    loadPostponedDealsSearchParams,
    postponedDealsSearchParamUrlKeys,
    postponedDealsSearchParams,
} from './search-params';
import { ISearchByQueryFormProps } from './types';

import type { TPostponedDealsQueryParams, TPostponedDealsSortField } from '@crm/entities/deal';
import type { DateRange } from 'react-day-picker';

interface SearchFormValues {
    query: string;
}

export const useFilterByQuery = ({ limit }: { limit: number }) => {
    const [, setQueryParams] = useQueryStates(postponedDealsSearchParams, {
        history: 'push',
        urlKeys: postponedDealsSearchParamUrlKeys,
    });
    const optimisticSearchParams = useOptimisticSearchParams();
    const searchParams = loadPostponedDealsSearchParams(optimisticSearchParams);
    const apiSort = searchParams.sort ?? DEFAULT_POSTPONED_DEALS_SORT;
    const apiDirection = searchParams.direction ?? DEFAULT_POSTPONED_DEALS_DIRECTION;
    const queryParams = {
        ...searchParams,
        direction: apiDirection,
        limit,
        sort: apiSort,
    };
    const { handleSubmit, register, reset } = useForm<SearchFormValues>({
        defaultValues: { query: queryParams.query ?? '' },
    });

    useEffect(() => {
        reset({ query: queryParams.query ?? '' });
    }, [queryParams.query, reset]);

    const onSubmit = handleSubmit(({ query: formQuery }) => {
        const normalizedQuery = formQuery.trim();

        void setQueryParams({
            query: normalizedQuery || null,
            page: 1,
        });
    });

    const resetFilters = () => {
        reset({ query: '' });
        void setQueryParams({
            query: null,
            sort: null,
            direction: null,
            postponedUntilFrom: null,
            postponedUntilTo: null,
            lastContactAtFrom: null,
            lastContactAtTo: null,
            areaFrom: null,
            areaTo: null,
            page: 1,
        });
    };

    const onPostponedUntilDateFilterChange = (date?: DateRange) => {
        const { from, to } = serializeDateRangeQuery(date);

        void setQueryParams({
            postponedUntilFrom: from,
            postponedUntilTo: to,
            page: 1,
        });
    };

    const onLastContactAtDateFilterChange = (date?: DateRange) => {
        const { from, to } = serializeDateRangeQuery(date);

        void setQueryParams({
            lastContactAtFrom: from,
            lastContactAtTo: to,
            page: 1,
        });
    };

    const onAreaRangeChange = ({ from, to }: { from?: string; to?: string }) => {
        const normalizedAreaFrom = from?.trim() || null;
        const normalizedAreaTo = to?.trim() || null;

        void setQueryParams({
            areaFrom: normalizedAreaFrom,
            areaTo: normalizedAreaTo,
            page: 1,
        });
    };

    const onSortChange = ({
        direction,
        sort,
    }: {
        direction: TPostponedDealsQueryParams['direction'] | null;
        sort: TPostponedDealsSortField | null;
    }) => {
        void setQueryParams({ direction, sort });
    };

    const onPageChange = (page: number) => {
        void setQueryParams({ page });
    };

    const onPageNormalize = (page: number) => {
        void setQueryParams({ page }, { history: 'replace' });
    };

    const hasDataFilters = Boolean(
        queryParams.query ||
            queryParams.postponedUntilFrom ||
            queryParams.postponedUntilTo ||
            queryParams.lastContactAtFrom ||
            queryParams.lastContactAtTo ||
            queryParams.areaFrom ||
            queryParams.areaTo
    );
    return {
        hasDataFilters,
        queryParams: queryParams satisfies TPostponedDealsQueryParams,
        resetFilters,
        dateFilterProps: {
            postponedUntil: {
                value: parseDateRangeQuery(
                    queryParams.postponedUntilFrom,
                    queryParams.postponedUntilTo
                ),
                onApply: onPostponedUntilDateFilterChange,
            },
            lastContactAt: {
                value: parseDateRangeQuery(
                    queryParams.lastContactAtFrom,
                    queryParams.lastContactAtTo
                ),
                onApply: onLastContactAtDateFilterChange,
            },
        },
        areaRangeProps: {
            value: {
                from: queryParams.areaFrom ?? '',
                to: queryParams.areaTo ?? '',
            },
            onChange: onAreaRangeChange,
        },
        tableSortProps: {
            allowClear: DEALS_TABLE_ALLOW_SORT_CLEAR,
            sort: DEALS_TABLE_ALLOW_SORT_CLEAR ? searchParams.sort : apiSort,
            direction: DEALS_TABLE_ALLOW_SORT_CLEAR ? searchParams.direction : apiDirection,
            onSortChange,
        },
        paginationProps: {
            onPageChange,
            onPageNormalize,
        },
        searchFormProps: {
            inputProps: register(SEARCH_QUERY_PARAM),
            onSubmit,
        } satisfies ISearchByQueryFormProps,
    };
};
